import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Box,
  Typography,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Chip
} from '@mui/material';
import {
  Close,
  Add,
  Edit,
  Save,
  ContentCopy
} from '@mui/icons-material';
import { createBlogPost, updateBlogPost } from '../services/blogService';

const BlogDialog = ({
  open,
  onClose,
  mode = 'create', // 'create', 'view', 'edit'
  blogPost = null,
  onSuccess = () => { }
}) => {
  const [blogForm, setBlogForm] = useState({
    title: '',
    content: '',
    featuredImage: '',
    tags: [],
    mediaTitle: '',
    mediaDescription: '',
    mediaType: 'image', // 'image' or 'video'
    published: true
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  // Initialize form data when dialog opens or mode changes
  useEffect(() => {
    if (open) {
      if (mode === 'create') {
        setBlogForm({
          title: '',
          content: 'Headline 1\nHeadline 2\n',
          featuredImage: '',
          tags: [],
          mediaTitle: '',
          mediaDescription: '',
          mediaType: 'image',
          published: true
        });
      } else if ((mode === 'view' || mode === 'edit') && blogPost) {
        setBlogForm({
          title: blogPost.title || '',
          content: blogPost.content || '',
          featuredImage: blogPost.featuredImage || '',
          tags: blogPost.tags || [],
          mediaTitle: blogPost.mediaTitle || '',
          mediaDescription: blogPost.mediaDescription || '',
          mediaType: blogPost.mediaType || 'image',
          published: blogPost.published !== undefined ? blogPost.published : true
        });
      }
      setError('');
      setSuccess(false);
      setSelectedFile(null);
    }
  }, [open, mode, blogPost]);

  const handleClose = () => {
    if (!loading) {
      onClose();
      setError('');
      setSuccess(false);
      setSelectedFile(null);
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleSubmit = async () => {
    if (!blogForm.title.trim()) {
      setError('Blog title is required');
      return;
    }

    if (!blogForm.content.trim()) {
      setError('Blog content is required');
      return;
    }

    try {
      setLoading(true);
      setError('');

      const postData = {
        title: blogForm.title,
        content: blogForm.content,
        tags: blogForm.tags,
        featuredImage: blogForm.featuredImage || undefined,
        mediaTitle: blogForm.mediaTitle || undefined,
        mediaDescription: blogForm.mediaDescription || undefined,
        mediaType: blogForm.mediaType,
        published: blogForm.published
      };

      if (mode === 'create') {
        await createBlogPost(postData);
        setSuccess(true);
        setTimeout(() => {
          onSuccess();
          handleClose();
        }, 1500);
      } else if (mode === 'edit') {
        await updateBlogPost(blogPost._id, postData);
        setSuccess(true);
        setTimeout(() => {
          onSuccess();
          handleClose();
        }, 1500);
      }
    } catch (err) {
      console.error('Error saving blog post:', err);
      setError('Failed to save blog post. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const isReadOnly = mode === 'view';
  const isEditing = mode === 'edit';
  const isCreating = mode === 'create';

  const getDialogTitle = () => {
    switch (mode) {
      case 'create': return 'Create New Blog Post';
      case 'edit': return 'Edit Blog Post';
      case 'view': return blogPost?.title || 'View Blog Post';
      default: return 'Blog Post';
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle>
        {getDialogTitle()}
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
          disabled={loading}
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {isCreating ? 'Blog post created successfully!' : 'Blog post updated successfully!'}
          </Alert>
        )}

        <Grid container spacing={3} sx={{ mt: 1 }}>
          {/* Left Side - Blog Input Fields */}
          <Grid item xs={12} md={6}>
            <Box sx={{ mb: 3 }}>
              {/* Blog Title */}
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                BLOG TITLE
              </Typography>
              <TextField
                fullWidth
                value={blogForm.title}
                onChange={(e) => setBlogForm({ ...blogForm, title: e.target.value })}
                placeholder="How to get Protein in Your Body"
                sx={{ mb: 2 }}
                InputProps={{ readOnly: isReadOnly }}
                required
              />

              {/* Blog Content */}
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                BLOG CONTENT
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={8}
                value={blogForm.content}
                onChange={(e) => setBlogForm({ ...blogForm, content: e.target.value })}
                placeholder="Write your blog post content here..."
                sx={{ mb: 2 }}
                InputProps={{ readOnly: isReadOnly }}
                required
              />

              {/* Tags */}
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                TAGS
              </Typography>
              <TextField
                fullWidth
                value={blogForm.tags.join(', ')}
                onChange={(e) => setBlogForm({
                  ...blogForm,
                  tags: e.target.value.split(',').map(tag => tag.trim()).filter(tag => tag)
                })}
                placeholder="health, fitness, protein"
                sx={{ mb: 2 }}
                InputProps={{ readOnly: isReadOnly }}
                helperText="Separate tags with commas"
              />

              {/* Display Tags */}
              {blogForm.tags.length > 0 && (
                <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {blogForm.tags.map((tag, index) => (
                    <Chip
                      key={index}
                      label={tag}
                      size="small"
                      onDelete={!isReadOnly ? () => {
                        setBlogForm({
                          ...blogForm,
                          tags: blogForm.tags.filter((_, i) => i !== index)
                        });
                      } : undefined}
                    />
                  ))}
                </Box>
              )}
            </Box>
          </Grid>

          {/* Right Side - Media Section */}
          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom>
              Media
            </Typography>

            {!isReadOnly && (
              <>
                {/* Media Type Selection */}
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Media Type</InputLabel>
                  <Select
                    value={blogForm.mediaType}
                    label="Media Type"
                    onChange={(e) => {
                      setBlogForm({ ...blogForm, mediaType: e.target.value });
                      setSelectedFile(null);
                    }}
                  >
                    <MenuItem value="image">Image</MenuItem>
                    <MenuItem value="video">Video</MenuItem>
                  </Select>
                </FormControl>

                {/* File Upload Section */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Upload {blogForm.mediaType === 'video' ? 'Video' : 'Image'} File
                  </Typography>

                  <input
                    type="file"
                    accept={blogForm.mediaType === 'video' ? 'video/*' : 'image/*'}
                    onChange={handleFileSelect}
                    style={{ display: 'none' }}
                    id="media-file-input"
                  />
                  <label htmlFor="media-file-input">
                    <Button
                      variant="outlined"
                      component="span"
                      fullWidth
                      sx={{ mb: 1 }}
                    >
                      Choose {blogForm.mediaType === 'video' ? 'Video' : 'Image'} File
                    </Button>
                  </label>

                  {selectedFile && (
                    <Box sx={{
                      mb: 1,
                      p: 2,
                      backgroundColor: '#e3f2fd',
                      borderRadius: 1,
                      border: '1px solid #1976d2'
                    }}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                        📁 {selectedFile.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Size: {(selectedFile.size / 1024 / 1024).toFixed(2)} MB | Type: {selectedFile.type}
                      </Typography>
                    </Box>
                  )}

                  <Button
                    variant="contained"
                    disabled={!selectedFile || uploading}
                    fullWidth
                    sx={{ mb: 1 }}
                  >
                    {uploading ? (
                      <>
                        <CircularProgress size={20} sx={{ mr: 1 }} />
                        Uploading...
                      </>
                    ) : (
                      `Upload ${blogForm.mediaType === 'video' ? 'Video' : 'Image'}`
                    )}
                  </Button>
                </Box>
              </>
            )}



            {/* Media Preview */}
            {blogForm.featuredImage && (
              <Box sx={{ mb: 2, textAlign: 'center' }}>
                {blogForm.mediaType === 'video' ? (
                  <video
                    src={blogForm.featuredImage}
                    controls
                    style={{
                      maxWidth: '100%',
                      maxHeight: '300px',
                      borderRadius: '8px'
                    }}
                  />
                ) : (
                  <img
                    src={blogForm.featuredImage}
                    alt="Preview"
                    style={{
                      maxWidth: '100%',
                      maxHeight: '300px',
                      borderRadius: '8px'
                    }}
                  />
                )}
              </Box>
            )}

            {/* Media Title */}
            <TextField
              fullWidth
              label="Title"
              value={blogForm.mediaTitle}
              onChange={(e) => setBlogForm({ ...blogForm, mediaTitle: e.target.value })}
              placeholder="Enter title for the media"
              sx={{ mb: 2 }}
              InputProps={{ readOnly: isReadOnly }}
            />

            {/* Media Description */}
            <TextField
              fullWidth
              label="Description"
              multiline
              rows={4}
              value={blogForm.mediaDescription}
              onChange={(e) => setBlogForm({ ...blogForm, mediaDescription: e.target.value })}
              placeholder="Enter description for the media"
              sx={{ mb: 2 }}
              InputProps={{ readOnly: isReadOnly }}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button
          onClick={handleClose}
          disabled={loading}
        >
          {isReadOnly ? 'Close' : 'Cancel'}
        </Button>

        {isReadOnly && blogPost && (
          <Button
            variant="contained"
            onClick={() => {
              // Switch to edit mode
              handleClose();
              // This would be handled by the parent component
            }}
          >
            Edit
          </Button>
        )}

        {!isReadOnly && (
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={loading || !blogForm.title.trim() || !blogForm.content.trim()}
            startIcon={loading ? <CircularProgress size={20} /> : (isCreating ? <Add /> : <Save />)}
          >
            {loading ? (isCreating ? 'Creating...' : 'Saving...') : (isCreating ? 'Create Blog Post' : 'Update Blog Post')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default BlogDialog;
